import React from 'react';
import { Client } from '../../types/client';
import { getClientInitials, getClientStatusColor, filterClients } from '../../utils/clientUtils';

interface ClientListProps {
  clients: Client[];
  selectedClient: Client | null;
  searchTerm: string;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalClients: number;
  onClientSelect: (client: Client) => void;
  onSearchChange: (term: string) => void;
  onCreateClient: () => void;
  onLoadMore: () => void;
}

const ClientList: React.FC<ClientListProps> = ({
  clients,
  selectedClient,
  searchTerm,
  loading,
  loadingMore,
  hasMore,
  totalClients,
  onClientSelect,
  onSearchChange,
  onCreateClient,
  onLoadMore
}) => {
  const filteredClients = filterClients(clients, searchTerm);

  if (loading) {
    return (
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute left-3 top-2.5 text-gray-400">
              🔍
            </div>
          </div>
          <button
            onClick={onCreateClient}
            className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center text-lg font-medium transition-colors"
            title="Add New Client"
          >
            +
          </button>
        </div>
      </div>

      {/* Client List - Restructured to match working TranscriptList pattern */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {filteredClients.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-gray-400 text-3xl mb-3">👥</div>
              <p className="text-gray-600 text-sm">No clients found</p>
            </div>
          ) : (
            <div className="space-y-2 p-2">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => onClientSelect(client)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedClient?.id === client.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-700">
                      {getClientInitials(client.first_name, client.last_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {client.first_name} {client.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {client.email || client.phone_primary}
                      </p>
                      {client.company_name && (
                        <p className="text-xs text-gray-400 truncate">
                          {client.company_name}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClientStatusColor(
                          client.client_status || 'active'
                        )}`}
                      >
                        {client.client_status || 'active'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Load More Button - Outside scrollable area */}
        {!searchTerm && hasMore && (
          <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Loading...
                </div>
              ) : (
                `Load More (${totalClients - clients.length} remaining)`
              )}
            </button>
          </div>
        )}
        
        {/* Pagination Info - Outside scrollable area */}
        {!searchTerm && filteredClients.length > 0 && (
          <div className="p-2 text-xs text-gray-500 text-center bg-gray-50 border-t border-gray-200 flex-shrink-0">
            Showing {clients.length} of {totalClients} clients
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientList; 
